import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { adminApi, catalogApi } from '@/api';
import {
  Badge,
  BoxAvatar,
  BoxList,
  BoxRow,
  Button,
  CardActions,
  EmptyState,
  ErrorState,
  Icon,
  Input,
  ListCard,
  LoadingState,
  Modal,
  PageHeader,
  Pagination,
  SearchInput,
  SegmentedTabs,
  Select,
  ViewButton,
  useToast,
} from '@/components/ui';
import { VERIFICATION_TONES, humanizeStatus } from '@/features/admin/status';
import { paths } from '@/routes/paths';
import { errorMessage } from '@/hooks/useApiError';
import { formatDate } from '@/utils/format';

const TABS = [
  { key: 'PENDING', label: 'Pending' },
  { key: 'VERIFIED', label: 'Verified' },
  { key: 'REJECTED', label: 'Rejected' },
  { key: 'UNVERIFIED', label: 'Not submitted' },
];

const PAGE_SIZE = 20;

export function AdminVerificationsPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [status, setStatus] = useState('PENDING');
  const [search, setSearch] = useState('');
  const [industry, setIndustry] = useState('');
  const [page, setPage] = useState(1);
  const [rejecting, setRejecting] = useState(null);
  const [reason, setReason] = useState('');

  const params = {
    page,
    page_size: PAGE_SIZE,
    statuses: status,
    ...(search.trim() ? { q: search.trim() } : {}),
    ...(industry ? { industry_id: industry } : {}),
  };

  const { data, isPending, isPlaceholderData, isError, error, refetch } = useQuery({
    queryKey: ['admin-verifications', params],
    queryFn: () => adminApi.listVerifications(params),
    // Without this every keystroke unmounts the page for the loading state,
    // taking the search box and the focus in it.
    placeholderData: keepPreviousData,
  });

  const { data: industries } = useQuery({
    queryKey: ['industries'],
    queryFn: () => catalogApi.listIndustries(),
  });

  /** Every filter change is a new result set, so page 1 is the only sane page. */
  function change(setter) {
    return (event) => {
      setter(event.target.value);
      setPage(1);
    };
  }

  const activeCount = [search !== '', industry !== ''].filter(Boolean).length;

  function resetFilters() {
    setSearch('');
    setIndustry('');
    setPage(1);
  }

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: ['admin-verifications'] });
    void queryClient.invalidateQueries({ queryKey: ['admin-overview'] });
  }

  const approve = useMutation({
    mutationFn: (id) => adminApi.approveCompany(id),
    onSuccess: (company) => {
      toast.success(`${company.name} verified`);
      invalidate();
    },
    onError: (approveError) =>
      toast.error(errorMessage(approveError, 'Could not verify the company')),
  });

  const reject = useMutation({
    mutationFn: ({ id, reason: text }) => adminApi.rejectCompany(id, text),
    onSuccess: () => {
      toast.success('Verification rejected');
      invalidate();
      setRejecting(null);
      setReason('');
    },
    onError: (rejectError) =>
      toast.error(errorMessage(rejectError, 'Could not reject the request')),
  });

  const feature = useMutation({
    mutationFn: ({ id, isFeatured }) => adminApi.setCompanyFeatured(id, isFeatured),
    onSuccess: () => {
      toast.success('Featured companies updated');
      invalidate();
    },
    onError: (featureError) =>
      toast.error(errorMessage(featureError, 'Could not update featuring')),
  });

  const rows = data?.data ?? [];
  const meta = data?.meta;

  return (
    // On a phone or tablet held upright the list card runs to the bottom of the
    // screen.
    <div className="flex flex-col portrait:flex-1">
      <PageHeader
        title="Verify companies"
        description="Only verified companies can post internships, so check the details."
      />

      <ListCard
        fill
        className="flex-1"
        isRefreshing={isPlaceholderData}
        toolbarStart={
          <SegmentedTabs
            variant="plain"
            tabs={TABS}
            value={status}
            onChange={(key) => {
              setStatus(key);
              setPage(1);
            }}
            label="Verification status"
          />
        }
        toolbarEnd={
          <>
            <Select
              label="Industry"
              hideLabel
              size="sm"
              wrapperClassName="w-full sm:w-44"
              value={industry}
              onChange={change(setIndustry)}
            >
              <option value="">Any industry</option>
              {(industries ?? []).map((row) => (
                <option key={row.id} value={row.id}>
                  {row.name}
                </option>
              ))}
            </Select>
            <SearchInput
              label="Search companies"
              hideLabel
              size="sm"
              className="w-full sm:w-64"
              placeholder="Company, location or the person who submitted"
              value={search}
              onChange={change(setSearch)}
              onClear={() => {
                setSearch('');
                setPage(1);
              }}
            />
            {activeCount > 0 && (
              <Button variant="ghost" size="sm" onClick={resetFilters}>
                <Icon name="reset" size={14} />
                Reset
              </Button>
            )}
          </>
        }
        state={
          isError ? (
            <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />
          ) : isPending ? (
            <LoadingState label="Loading the verification queue…" />
          ) : rows.length === 0 ? (
            activeCount > 0 ? (
              <EmptyState
                title="Nothing matches these filters."
                description="No companies in this state match these filters."
                action={
                  <Button variant="secondary" size="sm" onClick={resetFilters}>
                    Reset filters
                  </Button>
                }
              />
            ) : (
              <EmptyState
                title="Nothing here"
                description="No companies in this state right now."
              />
            )
          ) : null
        }
        footer={
          meta && (
            <Pagination
              className=""
              page={meta.page}
              pageSize={PAGE_SIZE}
              totalPages={meta.total_pages}
              totalItems={meta.total_items}
              onPageChange={setPage}
            />
          )
        }
      >
        <BoxList>
          {rows.map((row) => (
            <BoxRow
              key={row.id}
              accent={VERIFICATION_TONES[row.verification_status] ?? 'neutral'}
              leading={<BoxAvatar name={row.name} />}
              title={row.name}
              to={paths.admin.companyDetail(row.id)}
              badges={
                <>
                  <Badge dot tone={VERIFICATION_TONES[row.verification_status] ?? 'neutral'}>
                    {humanizeStatus(row.verification_status)}
                  </Badge>
                  {row.is_featured && <Badge tone="brand">Featured</Badge>}
                </>
              }
              subtitle={
                [row.industry, row.location, row.company_size].filter(Boolean).join(' · ') ||
                'No industry or location yet'
              }
              facts={[
                row.owner_name && { icon: 'user', text: `Submitted by ${row.owner_name}` },
                row.owner_email && { icon: 'send', text: row.owner_email },
                { icon: 'userCheck', text: `${row.profile_completion}% profile` },
                {
                  icon: 'calendar',
                  text: row.submitted_at
                    ? `Submitted ${formatDate(row.submitted_at)}`
                    : 'Not submitted',
                },
              ]}
              // Undecided: the decision is the main action, so View steps
              // back and Verify sits last. Decided: View is the way in.
              actions={
                <CardActions>
                  <ViewButton
                    to={paths.admin.companyDetail(row.id)}
                    title={`Open ${row.name}`}
                    variant={row.verification_status === 'PENDING' ? 'secondary' : 'tintBrand'}
                  />
                  {/* Only a submitted request can be decided: an unsubmitted or
                      rejected company has nothing waiting to be judged. */}
                  {row.verification_status === 'PENDING' && (
                    <>
                      <Button
                        variant="tintDanger"
                        size="sm"
                        title={`Reject ${row.name}`}
                        onClick={() => setRejecting(row)}
                      >
                        <Icon name="close" size={14} />
                        Reject
                      </Button>
                      <Button
                        variant="tintPositive"
                        size="sm"
                        title={`Verify ${row.name}`}
                        isLoading={approve.isPending && approve.variables === row.id}
                        onClick={() => approve.mutate(row.id)}
                      >
                        <Icon name="check" size={14} />
                        Verify
                      </Button>
                    </>
                  )}
                </CardActions>
              }
              // A toggle, so it sits in the corner like a bookmark. Only a
              // verified company can be put on the landing page.
              corner={
                row.verification_status === 'VERIFIED' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-pressed={Boolean(row.is_featured)}
                    title={
                      row.is_featured
                        ? 'Remove from the landing page'
                        : 'Feature on the landing page'
                    }
                    className={row.is_featured ? 'text-warning-600' : undefined}
                    isLoading={feature.isPending && feature.variables?.id === row.id}
                    onClick={() => feature.mutate({ id: row.id, isFeatured: !row.is_featured })}
                  >
                    <Icon name="star" size={14} filled={Boolean(row.is_featured)} />
                    {row.is_featured ? 'Featured' : 'Feature'}
                  </Button>
                )
              }
            />
          ))}
        </BoxList>
      </ListCard>

      <Modal
        open={Boolean(rejecting)}
        title={`Reject ${rejecting?.name}`}
        description="The reason is recorded in the audit log."
        onClose={() => setRejecting(null)}
      >
        <div className="space-y-4">
          <Input
            label="Reason"
            required
            placeholder="Registration number does not match public records"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setRejecting(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              disabled={reason.trim().length < 4}
              isLoading={reject.isPending}
              onClick={() => reject.mutate({ id: rejecting.id, reason: reason.trim() })}
            >
              Reject
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
